/**
 * Call-to-Action (CTA) Section Component
 *
 * Attention-grabbing section with headline, description, and action buttons.
 * Built with Lightning Velocity design tokens.
 */

import React from 'react';
import { clsx } from 'clsx';
import { Button } from '../components/Button';

export interface CTAProps {
  /**
   * Optional badge/label above headline
   */
  badge?: string;

  /**
   * Main headline
   */
  headline: string;

  /**
   * Supporting description
   */
  description?: string;

  /**
   * Primary CTA button text
   */
  primaryCta: string;

  /**
   * Primary CTA button click handler
   */
  onPrimaryCta?: () => void;

  /**
   * Secondary CTA button text (optional)
   */
  secondaryCta?: string;

  /**
   * Secondary CTA button click handler
   */
  onSecondaryCta?: () => void;

  /**
   * Background variant
   */
  background?: 'gradient' | 'solid' | 'subtle' | 'white';

  /**
   * Size variant
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Text alignment
   */
  align?: 'left' | 'center';

  /**
   * Optional image/illustration
   */
  image?: React.ReactNode;
}

/**
 * CTA section using Lightning Velocity design system
 */
export const CTA: React.FC<CTAProps> = ({
  badge,
  headline,
  description,
  primaryCta,
  onPrimaryCta,
  secondaryCta,
  onSecondaryCta,
  background = 'gradient',
  size = 'md',
  align = 'center',
  image,
}) => {
  // Background styles
  const backgroundStyles = {
    gradient: 'bg-gradient-to-br from-brand-primary via-brand-primary-dark to-brand-accent',
    solid: 'bg-brand-primary',
    subtle: 'bg-neutral-50',
    white: 'bg-white',
  };

  // Text color based on background
  const textColor = ['gradient', 'solid'].includes(background)
    ? 'text-white'
    : 'text-neutral-900';

  const descriptionColor = ['gradient', 'solid'].includes(background)
    ? 'text-white/90'
    : 'text-neutral-600';

  // Size padding
  const sizeStyles = {
    sm: 'py-12 md:py-16',
    md: 'py-16 md:py-24',
    lg: 'py-20 md:py-32',
  };

  // Alignment styles
  const alignmentStyles = {
    left: 'text-left',
    center: 'text-center mx-auto',
  };

  const buttonAlignment = {
    left: 'justify-start',
    center: 'justify-center',
  };

  return (
    <section className={clsx(
      'relative overflow-hidden',
      backgroundStyles[background],
      sizeStyles[size]
    )}>
      {/* Decorative background elements */}
      {['gradient', 'solid'].includes(background) && (
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
        </div>
      )}

      <div className="container mx-auto px-4 relative z-10">
        <div className={clsx(
          'grid gap-8 items-center',
          image ? 'lg:grid-cols-2' : 'lg:grid-cols-1'
        )}>
          {/* Content */}
          <div className={clsx(
            'space-y-6',
            alignmentStyles[align],
            !image && 'max-w-3xl'
          )}>
            {/* Badge */}
            {badge && (
              <div className={clsx(
                'inline-block px-4 py-2 rounded-full text-sm font-semibold',
                ['gradient', 'solid'].includes(background)
                  ? 'bg-white/20 text-white'
                  : 'bg-brand-primary/10 text-brand-primary'
              )}>
                {badge}
              </div>
            )}

            {/* Headline */}
            <h2 className={clsx(
              'text-display-l md:text-display-xl font-display font-bold leading-tight',
              textColor
            )}>
              {headline}
            </h2>

            {/* Description */}
            {description && (
              <p className={clsx(
                'text-lg md:text-xl leading-relaxed max-w-2xl',
                descriptionColor,
                align === 'center' && 'mx-auto'
              )}>
                {description}
              </p>
            )}

            {/* CTAs */}
            <div className={clsx(
              'flex flex-col sm:flex-row gap-4',
              buttonAlignment[align]
            )}>
              <Button
                variant={['gradient', 'solid'].includes(background) ? 'secondary' : 'primary'}
                size="lg"
                onClick={onPrimaryCta}
              >
                {primaryCta}
              </Button>

              {secondaryCta && (
                <Button
                  variant={['gradient', 'solid'].includes(background) ? 'ghost' : 'outline'}
                  size="lg"
                  onClick={onSecondaryCta}
                  className={['gradient', 'solid'].includes(background) ? 'text-white border-white hover:bg-white/10' : ''}
                >
                  {secondaryCta}
                </Button>
              )}
            </div>
          </div>

          {/* Image/Illustration */}
          {image && (
            <div className="relative">
              {image}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

CTA.displayName = 'CTA';

export default CTA;
